import type {
  MassPublishActivityResult,
  MassPublishActivityResponse,
} from "../models/Activity";
import type {
  MassPublishImageResult,
  MassPublishImageResponse,
} from "../models/Image";
import type {
  MassPublishToolResult,
  MassPublishToolResponse,
} from "../models/Tool";

export type EntityType = "activity" | "image" | "tool";

export type MassPublishResult =
  MassPublishActivityResult | MassPublishImageResult | MassPublishToolResult;

export type MassPublishResponse =
  | MassPublishActivityResponse
  | MassPublishImageResponse
  | MassPublishToolResponse;

interface MassPublishTranslationKeys {
  dialogTitle: string;
  resultTitle: string;
  errorTitle: string;
  generalError: string;
  summary: string;
  itemSuccess: string;
  itemError: string;
  count: string;
  description: string;
}

type Translate = (key: string, options?: { defaultValue?: string }) => string;

const massPublishTranslationKeys: Record<
  EntityType,
  MassPublishTranslationKeys
> = {
  activity: {
    dialogTitle: "massPublishDialogTitle",
    resultTitle: "massPublishResultsTitle",
    errorTitle: "massPublishErrorTitle",
    generalError: "massPublishGeneralError",
    summary: "massPublishSummary",
    itemSuccess: "massPublishItemSuccess",
    itemError: "massPublishItemError",
    count: "massPublishCount",
    description: "massPublishingDescription",
  },
  image: {
    dialogTitle: "massPublishImageDialogTitle",
    resultTitle: "massPublishImageResultsTitle",
    errorTitle: "massPublishImageErrorTitle",
    generalError: "massPublishImageGeneralError",
    summary: "massPublishImageSummary",
    itemSuccess: "massPublishImageItemSuccess",
    itemError: "massPublishImageItemError",
    count: "massPublishImageCount",
    description: "massPublishImageDescription",
  },
  tool: {
    dialogTitle: "massPublishToolDialogTitle",
    resultTitle: "massPublishToolResultsTitle",
    errorTitle: "massPublishToolErrorTitle",
    generalError: "massPublishToolGeneralError",
    summary: "massPublishToolSummary",
    itemSuccess: "massPublishToolItemSuccess",
    itemError: "massPublishToolItemError",
    count: "massPublishToolCount",
    description: "massPublishToolDescription",
  },
};

export const getMassPublishTranslationKeys = (entityType: EntityType) =>
  massPublishTranslationKeys[entityType];

export const getPublishedItemTitle = (
  entityType: EntityType,
  result: MassPublishResult,
  t: Translate
) => {
  switch (entityType) {
    case "activity":
      return (
        (result as MassPublishActivityResult).publishedActivity?.title ||
        t("untitledActivity")
      );
    case "image":
      return (
        (result as MassPublishImageResult).publishedImage?.title ||
        t("untitledImage")
      );
    case "tool":
      return (
        (result as MassPublishToolResult).publishedTool?.title ||
        t("untitledTool")
      );
  }
};

export const getResultErrorMessage = (
  result: MassPublishResult,
  t: Translate
) => {
  if (!result.errorTranslationKey) {
    return result.errorMessage || t("unknownError");
  }

  return t(result.errorTranslationKey, {
    defaultValue: result.errorMessage || t("unknownError"),
  });
};
